import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Chip,
  Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { BRAND } from '../../config/brand';
import {
  useGetWorkOrderDetailQuery,
  useAdvanceStageMutation,
  useApproveDesignMutation,
  useAssignMachineToStageMutation,
  useCompleteStageMutation,
  useScheduleDeliveryMutation,
  useGenerateInvoiceMutation,
} from '../../api/productionFlowApi';
import { useGetAvailableMachinesQuery } from '../../api/machineApi';
import { useRbac } from '../../context/RbacContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return value === index ? <Box sx={{ py: 2 }}>{children}</Box> : null;
}

export const WorkOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = location.pathname.startsWith('/manager') ? '/manager' : '/admin';
  const { user } = useRbac();
  const [tab, setTab] = useState(0);
  const [selectedMachine, setSelectedMachine] = useState('');

  const { data: wo, isLoading, error, refetch } = useGetWorkOrderDetailQuery(id!, { skip: !id });
  const { data: machines } = useGetAvailableMachinesQuery();
  const [advanceStage] = useAdvanceStageMutation();
  const [approveDesign] = useApproveDesignMutation();
  const [assignMachine] = useAssignMachineToStageMutation();
  const [completeStage] = useCompleteStageMutation();
  const [scheduleDelivery] = useScheduleDeliveryMutation();
  const [generateInvoice] = useGenerateInvoiceMutation();

  const workOrder = wo as Record<string, any> | undefined;

  if (isLoading) return <CircularProgress sx={{ color: BRAND.colors.navy }} />;
  if (error || !workOrder) return <Alert severity="error">Work order not found</Alert>;

  const activeStage = workOrder.stageRecords?.find(
    (s: any) => s.stage === workOrder.currentStage && s.status !== 'completed',
  );

  const handleApprove = async () => {
    await approveDesign({ id: id!, userId: user?.id || '' });
    refetch();
  };

  const handleAdvance = async () => {
    await advanceStage(id!);
    refetch();
  };

  const handleAssignMachine = async () => {
    if (!activeStage || !selectedMachine) return;
    await assignMachine({ stageRecordId: activeStage.id, machineId: selectedMachine });
    refetch();
  };

  const handleCompleteStage = async () => {
    if (!activeStage) return;
    await completeStage({ stageRecordId: activeStage.id });
    refetch();
  };

  return (
    <Box>
      <Button onClick={() => navigate(`${basePath}/production/operations`)} sx={{ mb: 2 }}>
        ← Back to Operations Hub
      </Button>

      <Paper sx={{ p: 3, mb: 2, borderLeft: `6px solid ${BRAND.colors.navy}` }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h5" sx={{ color: BRAND.colors.navy, fontWeight: 700 }}>
              {workOrder.workOrderNumber}
            </Typography>
            <Typography color="text.secondary">
              Customer: {workOrder.order?.customer?.name} · Product: {workOrder.metadata?.productName}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
            <Chip label={workOrder.status} color="primary" sx={{ mr: 1 }} />
            <Chip label={workOrder.currentStage} variant="outlined" />
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {!workOrder.designApproved && (
            <Button variant="contained" onClick={handleApprove} sx={{ bgcolor: BRAND.colors.navy }}>
              Approve Design
            </Button>
          )}
          <Button variant="outlined" onClick={handleAdvance}>Advance Stage</Button>
          <Button variant="outlined" onClick={handleCompleteStage}>Complete Current Stage</Button>
          <Button variant="outlined" onClick={() => scheduleDelivery({ workOrderId: id!, data: {} })}>
            Schedule Delivery
          </Button>
          <Button variant="outlined" onClick={() => generateInvoice(id!)}>Generate Invoice</Button>
        </Box>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        {['Overview', 'Design Spec', 'Stages', 'Machines', 'Materials', 'Quality', 'Storage', 'Delivery', 'Invoice'].map((label, i) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      <TabPanel value={tab} index={0}>
        <Grid container spacing={2}>
          <Grid item xs={6} md={3}><Typography variant="caption">Product Type</Typography><Typography>{workOrder.productType}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption">Priority</Typography><Typography>P{workOrder.priority}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption">Line</Typography><Typography>{workOrder.productionLine?.name}</Typography></Grid>
          <Grid item xs={6} md={3}><Typography variant="caption">Design Approved</Typography><Typography>{workOrder.designApproved ? 'Yes' : 'No'}</Typography></Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        {workOrder.designSpecs?.map((spec: any) => (
          <Paper key={spec.id} sx={{ p: 2, mb: 1 }}>
            <Typography variant="subtitle2">Version {spec.version} — {spec.status}</Typography>
            <pre style={{ fontSize: 12 }}>{JSON.stringify(spec.specifications, null, 2)}</pre>
          </Paper>
        ))}
      </TabPanel>

      <TabPanel value={tab} index={2}>
        <Stepper orientation="vertical" activeStep={workOrder.stageRecords?.findIndex((s: any) => s.stage === workOrder.currentStage) ?? 0}>
          {workOrder.stageRecords?.map((stage: any) => (
            <Step key={stage.id} completed={stage.status === 'completed'} active={stage.status === 'active'}>
              <StepLabel>{stage.stage} — {stage.status}</StepLabel>
              <StepContent>
                {stage.machine && <Typography variant="caption">Machine: {stage.machine.name}</Typography>}
                {stage.notes && <Typography variant="caption" display="block">{stage.notes}</Typography>}
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </TabPanel>

      <TabPanel value={tab} index={3}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Assign Machine</InputLabel>
            <Select value={selectedMachine} label="Assign Machine" onChange={(e) => setSelectedMachine(e.target.value)}>
              {(machines as any[])?.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.name} ({m.type})</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleAssignMachine} disabled={!selectedMachine} sx={{ bgcolor: BRAND.colors.navy }}>
            Assign
          </Button>
        </Box>
        {workOrder.machineJobs?.map((job: any) => (
          <Paper key={job.id} sx={{ p: 1.5, mb: 1 }}>
            {job.machine?.name} — {job.jobType} — {job.status}
          </Paper>
        ))}
      </TabPanel>

      <TabPanel value={tab} index={4}><Typography color="text.secondary">Materials tracked via warehouse movements.</Typography></TabPanel>
      <TabPanel value={tab} index={5}><Typography color="text.secondary">Quality checks linked to order item QC records.</Typography></TabPanel>
      <TabPanel value={tab} index={6}><Typography color="text.secondary">Storage stage moves finished goods to warehouse.</Typography></TabPanel>

      <TabPanel value={tab} index={7}>
        {workOrder.deliveries?.length ? workOrder.deliveries.map((d: any) => (
          <Paper key={d.id} sx={{ p: 2, mb: 1 }}>{d.trackingNumber} — {d.status} — {d.carrier}</Paper>
        )) : <Typography color="text.secondary">No deliveries scheduled yet.</Typography>}
      </TabPanel>

      <TabPanel value={tab} index={8}>
        {workOrder.invoices?.length ? workOrder.invoices.map((inv: any) => (
          <Paper key={inv.id} sx={{ p: 2, mb: 1 }}>{inv.invoiceNumber} — ${inv.total} — {inv.status}</Paper>
        )) : <Typography color="text.secondary">No invoices generated yet.</Typography>}
      </TabPanel>
    </Box>
  );
};

export default WorkOrderDetail;
